import { movieList } from "@/mocks/movies";
import { tvshowList } from "@/mocks/tvshows";
import { newsList } from "@/mocks/news";
import type {
  ContentDetail,
  ContentKind,
  Movie,
  NewsArticle,
  SearchEntry,
  TvShow,
} from "@/types/media";

export const movies: Movie[] = movieList;
export const tvshows: TvShow[] = tvshowList;
export const newsArticles: NewsArticle[] = newsList;

export const ALL_MOVIE_GENRES = ["全部类型", ...uniqueValues(movies.flatMap((m) => m.genres))];
export const ALL_MOVIE_REGIONS = ["全部地区", ...uniqueValues(movies.map((m) => m.region))];
export const ALL_MOVIE_YEARS = ["全部年份", ...uniqueValues(movies.map((m) => String(m.year)))];

export const ALL_TV_GENRES = ["全部类型", ...uniqueValues(tvshows.flatMap((t) => t.genres))];
export const ALL_TV_REGIONS = ["全部地区", ...uniqueValues(tvshows.map((t) => t.region))];
export const ALL_TV_YEARS = ["全部年份", ...uniqueValues(tvshows.map((t) => String(t.year)))];

export function uniqueValues(values: string[]): string[] {
  return Array.from(new Set(values));
}

export function findDetail(id: string): ContentDetail | null {
  const movie = movies.find((item) => item.id === id);
  if (movie) return { kind: "movie", movie };

  const tvshow = tvshows.find((item) => item.id === id);
  if (tvshow) return { kind: "tvshow", tvshow };

  const news = newsArticles.find((item) => item.id === id);
  if (news) return { kind: "news", news };

  return null;
}

export function toSearchEntries(): SearchEntry[] {
  const movieEntries: SearchEntry[] = movies.map((item) => ({
    id: item.id,
    kind: "movie",
    title: item.title,
    year: item.year,
    cover: item.cover,
    summary: item.summary,
    tags: [`${item.year}`, item.region, ...item.genres],
  }));

  const tvEntries: SearchEntry[] = tvshows.map((item) => ({
    id: item.id,
    kind: "tvshow",
    title: item.title,
    year: item.year,
    cover: item.cover,
    summary: item.summary,
    tags: [`${item.year}`, item.region, ...item.genres, item.status],
  }));

  const newsEntries: SearchEntry[] = newsArticles.map((item) => ({
    id: item.id,
    kind: "news",
    title: item.title,
    cover: item.cover,
    summary: item.summary,
    tags: [formatDate(item.publishDate), item.source],
  }));

  return [...movieEntries, ...tvEntries, ...newsEntries];
}

export function searchContent(query: string, kind: ContentKind | "all"): SearchEntry[] {
  const keyword = query.trim().toLowerCase();
  if (!keyword) return [];

  return toSearchEntries().filter((entry) => {
    if (kind !== "all" && entry.kind !== kind) return false;
    const haystack = [entry.title, entry.summary, ...entry.tags].join(" ").toLowerCase();
    return haystack.includes(keyword);
  });
}

export function getRelated(id: string, limit = 6): SearchEntry[] {
  const detail = findDetail(id);
  if (!detail) return [];

  if (detail.kind === "news") {
    return newsArticles
      .filter((item) => item.id !== id)
      .sort((a, b) => b.publishDate.localeCompare(a.publishDate))
      .slice(0, limit)
      .map((item) => ({
        id: item.id,
        kind: "news" as ContentKind,
        title: item.title,
        cover: item.cover,
        summary: item.summary,
        tags: [formatDate(item.publishDate), item.source],
      }));
  }

  const pool: SearchEntry[] =
    detail.kind === "movie"
      ? movies.map((item) => ({
          id: item.id,
          kind: "movie" as ContentKind,
          title: item.title,
          year: item.year,
          cover: item.cover,
          summary: item.summary,
          tags: [`${item.year}`, item.region, ...item.genres],
        }))
      : tvshows.map((item) => ({
          id: item.id,
          kind: "tvshow" as ContentKind,
          title: item.title,
          year: item.year,
          cover: item.cover,
          summary: item.summary,
          tags: [`${item.year}`, item.region, ...item.genres],
        }));

  return pool
    .filter((item) => item.id !== id)
    .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))
    .slice(0, limit);
}

export function kindLabel(kind: ContentKind): string {
  if (kind === "movie") return "电影";
  if (kind === "tvshow") return "电视剧";
  return "资讯";
}

export function formatDate(iso: string): string {
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  return `${parts[0]}年${Number(parts[1])}月${Number(parts[2])}日`;
}

export function formatPopularity(value: number): string {
  if (value >= 10000) return `${(value / 10000).toFixed(1)}万`;
  return String(value);
}